import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { merge } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MeetingNotesPanelService } from '../services/meeting-notes-panel.service';

/**
 * The notes list's Organisation picker: tick any mix of organisations to see their notes, or All.
 * Used by the /tagged-notes page and the slide-in notes panel, which share the selection through
 * MeetingNotesPanelService.
 */
@Component({
  selector: 'app-note-org-picker',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatMenuModule, MatTooltipModule],
  // Both hosts are OnPush, so this redraws itself whenever the selection or the notes change.
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './note-org-picker.component.html',
  styleUrls: ['./note-org-picker.component.scss'],
})
export class NoteOrgPickerComponent {
  readonly notes = inject(MeetingNotesPanelService);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor() {
    merge(this.notes.orgScope$, this.notes.taggedNotes$)
      .pipe(takeUntilDestroyed(inject(DestroyRef)))
      .subscribe(() => this.cdr.markForCheck());
  }

  /**
   * orgOptions builds new objects on every read; without this the rows (and the box just clicked)
   * were torn down and rebuilt on each check.
   */
  trackOrg(_: number, org: { id: number }): number {
    return org.id;
  }

  get allSelected(): boolean {
    return this.notes.orgScope === 'all';
  }

  toggleAll(box: HTMLInputElement): void {
    this.notes.toggleAllOrgs();
    this.resync(box, this.allSelected);
  }

  toggle(id: number, box: HTMLInputElement): void {
    this.notes.toggleOrg(id);
    this.resync(box, this.notes.isOrgSelected(id));
  }

  only(id: number): void {
    this.notes.selectOnlyOrg(id);
    this.cdr.markForCheck();
  }

  /** Unread messages in the organisations left unticked. */
  hiddenUnread(): number {
    return this.notes.orgOptions
      .filter(o => !this.notes.isOrgSelected(o.id))
      .reduce((sum, o) => sum + o.unread, 0);
  }

  /**
   * The browser flips a checkbox on click before the selection is worked out; when the binding's
   * value comes out the same as before, Angular leaves the box as the browser left it. Set it from
   * the selection so a box never shows a state the list is not in.
   */
  private resync(box: HTMLInputElement, checked: boolean): void {
    box.checked = checked;
    this.cdr.markForCheck();
  }
}
